import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { toast } from "sonner";
import {
  LayoutDashboard, ClipboardList, LayoutGrid, Utensils, Wallet, Bike, Plug, Printer, PhoneIncoming,
  LifeBuoy, UserCog, ShieldCheck, Menu as MenuIcon, Volume2, VolumeX, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Brand";
import { useApi, errMsg, SOURCES } from "@/lib/api";
import { LogoutButton } from "@/components/auth/LogoutButton";
import { useClerkAvailable } from "@/components/auth/ClerkAuthProvider";
import { useLocalAuth } from "@/components/auth/LocalAuth";
import { playAlarm, unlockAudio } from "@/lib/sound";
import { printReceipt } from "@/lib/print";
import { PanelCtx } from "./PanelContext";
import NewOrderDialog from "./NewOrderDialog";
import CallPopup from "./CallPopup";

const NAV = [
  ["", "Özet", LayoutDashboard], ["siparisler", "Siparişler", ClipboardList], ["masalar", "Masalar & QR", LayoutGrid],
  ["menu", "Menü", Utensils], ["kasa", "Kasa & Giderler", Wallet], ["kuryeler", "Kurye Takip", Bike],
  ["entegrasyonlar", "Entegrasyonlar", Plug], ["yazici", "Yazıcı", Printer], ["caller-id", "Caller ID", PhoneIncoming],
  ["destek", "Destek Merkezi", LifeBuoy], ["hesap", "Hesap Ayarları", UserCog],
];

function SideNav({ isAdmin, onNav }) {
  const items = isAdmin ? [...NAV, ["admin", "Süper Yönetici", ShieldCheck]] : NAV;
  return (
    <nav className="flex flex-col gap-1 p-3">
      {items.map(([to, label, Icon]) => (
        <NavLink key={to} to={`/panel${to ? "/" + to : ""}`} end={!to} onClick={onNav} data-testid={`sidebar-${to || "ozet"}-link`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#FF6B0A] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
          <Icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />{label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ isAdmin, onNav }) {
  return (
    <div className="h-full flex flex-col bg-[#0F1F45] text-white">
      <div className="flex items-center gap-2 px-5 h-16 border-b border-white/10">
        <div className="bg-white rounded-xl p-0.5"><Logo className="h-9 w-9" /></div>
        <span className="font-display text-lg font-extrabold">Gazi <span className="text-[#FF6B0A]">Sepet</span></span>
      </div>
      <div className="flex-1 overflow-y-auto"><SideNav isAdmin={isAdmin} onNav={onNav} /></div>
    </div>
  );
}

export default function PanelLayout() {
  const api = useApi();
  const navigate = useNavigate();
  const clerkAvailable = useClerkAvailable();
  const { user: localUser } = useLocalAuth();
  const [me, setMe] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({ open: false, prefill: null });
  const [call, setCall] = useState(null);
  const [soundReady, setSoundReady] = useState(false);
  const seen = useRef(null);
  const bizRef = useRef(null);
  const mountedAt = useRef(new Date().toISOString());

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setMe(data);
      bizRef.current = data.business;
    } catch (e) { toast.error(errMsg(e)); }
  }, [api]);

  const pollOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders", { params: { active: true } });
      setActiveOrders(data);
      if (seen.current === null) { seen.current = new Set(data.map((o) => o.id)); return; }
      const fresh = data.filter((o) => !seen.current.has(o.id));
      fresh.forEach((o) => seen.current.add(o.id));
      const biz = bizRef.current;
      if (!fresh.length || !biz) return;
      if (biz.sound !== false) playAlarm();
      fresh.forEach((o) => toast.success(`Yeni sipariş #${o.no} • ${SOURCES[o.source] || o.source}`, { duration: 8000 }));
      if (biz.printer?.auto_print) {
        for (const o of fresh.filter((x) => !x.printed)) {
          printReceipt(o, biz);
          await api.patch(`/orders/${o.id}`, { printed: true });
        }
      }
    } catch { /* polling */ }
  }, [api]);

  const pollCalls = useCallback(async () => {
    try {
      const { data } = await api.get("/calls");
      const c = data.find((x) => !x.seen && x.at > mountedAt.current);
      if (c) {
        setCall((cur) => cur || c);
        if (bizRef.current?.sound !== false) playAlarm(3);
      }
    } catch { /* polling */ }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    pollOrders();
    const t1 = setInterval(pollOrders, 5000);
    const t2 = setInterval(pollCalls, 5000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [pollOrders, pollCalls]);

  const closeCall = async (c) => {
    setCall(null);
    await api.post(`/calls/${c.id}/seen`).catch(() => {});
  };

  if (!me) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="panel-loading">Panel yükleniyor...</div>;
  const biz = me.business;
  const isAdmin = me.user.role === "admin";
  const ctx = {
    api, me, biz, reload, activeOrders, refreshOrders: pollOrders, isAdmin,
    openNewOrder: (prefill = null) => setNewOrder({ open: true, prefill }),
  };

  return (
    <PanelCtx.Provider value={ctx}>
      <div className="min-h-screen flex bg-slate-50">
        <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen"><SidebarBody isAdmin={isAdmin} /></aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-64 border-0">
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <SidebarBody isAdmin={isAdmin} onNav={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-xl border-b flex items-center gap-3 px-4 md:px-6">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><MenuIcon /></Button>
            <div className="min-w-0">
              <p className="font-display font-bold truncate" data-testid="topbar-business-name">{biz.name}</p>
              <p className={`text-xs font-semibold ${me.license_active ? "text-emerald-600" : "text-amber-600"}`} data-testid="topbar-license-status">
                {me.license_active ? `Lisans aktif • ${new Date(biz.license_until).toLocaleDateString("tr-TR")}` : "Yıllık lisans aktif değil"}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" className={`rounded-full gap-1.5 ${soundReady ? "" : "border-[#FF6B0A] text-[#FF6B0A]"}`} data-testid="topbar-sound-btn"
                onClick={() => { unlockAudio(); setSoundReady(true); playAlarm(2); }}>
                {soundReady ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                <span className="hidden sm:inline">{soundReady ? "Ses açık" : "Sesi etkinleştir"}</span>
              </Button>
              <Button size="sm" className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-1.5" onClick={() => ctx.openNewOrder()} data-testid="topbar-new-order-btn">
                <Plus className="h-4 w-4" /><span className="hidden sm:inline">Yeni Sipariş</span>
              </Button>
              {localUser ? <LogoutButton /> : clerkAvailable ? <UserButton /> : null}
            </div>
          </header>
          {!me.license_active && (
            <div className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-6 py-2" data-testid="license-banner">
              Yıllık lisansınız (6.000 TL) aktif değil. Aktivasyon için <button className="underline font-semibold" onClick={() => navigate("/panel/destek")} data-testid="license-banner-support-btn">Destek Merkezi</button>'ne ulaşın.
            </div>
          )}
          <main className="flex-1 p-4 md:p-8"><Outlet /></main>
        </div>
      </div>
      <NewOrderDialog open={newOrder.open} prefill={newOrder.prefill} onOpenChange={(o) => setNewOrder((s) => ({ ...s, open: o }))} />
      {call && <CallPopup call={call} onClose={() => closeCall(call)} onOrder={() => { closeCall(call); ctx.openNewOrder({ customer_phone: call.phone, customer_name: call.name, address: call.address }); }} />}
    </PanelCtx.Provider>
  );
}
